import { divided, multi, plus } from "@/utils/calc";

export interface PickOutRowLike {
  pickOutNum?: number | null;
  deductOutCount?: number | null;
  maxOutNum?: number | null;
}

export const getGenerateCountLimit = (
  value: number | string | null | undefined
): number => {
  const limit = Number(value);

  if (Number.isNaN(limit) || limit < 0) return 0;

  return limit;
};

// 限制生成数量在合理范围内，确保不小于 1 且不大于最大值。
export const clampGenerateCount = (
  value: number | null | undefined,
  max: number
): number | null => {
  if (value === null || value === undefined) return null;

  const nextValue = Number(value);
  const limit = getGenerateCountLimit(max);

  if (Number.isNaN(nextValue)) return null;

  return Math.min(Math.max(nextValue, 1), limit);
};

const getNumber = (value: number | string | null | undefined): number => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? 0 : num;
};

// 直接使用需求物料行的用量和损耗率，不查询或匹配 BOM。
export const calcPickOutNumByBom = (
  generateCount: number,
  item: { bomAmount?: number | string | null; attritionRate?: number | string | null }
): number => {
  const quantity = getNumber(item.bomAmount);
  const rate = divided(getNumber(item.attritionRate).toString(), "100");
  const lossQuantity = multi(quantity.toString(), rate);
  const itemQuantity = plus(quantity.toString(), lossQuantity);
  return Math.ceil(Number(multi(generateCount.toString(), itemQuantity)));
};

export const clampPickOutDeductCount = (
  value: number | string | null | undefined,
  pickOutNum: number | string | null | undefined
): number | null => {
  // 行内扣减只能扣当前行本次预出库数量以内，避免最终数量被扣成负数。
  if (value === null || value === undefined) return null;

  const deductCount = getNumber(value);
  const maxDeductCount = Math.max(getNumber(pickOutNum), 0);

  return Math.min(Math.max(deductCount, 0), maxDeductCount);
};

export const getFinalPickOutNum = <T extends PickOutRowLike>(
  row: T,
  deductEnabled: boolean
): number => {
  // 本次预出库数量：全局未开启扣减时用 pickOutNum，开启后用扣减后的最终数量。
  const pickOutNum = getNumber(row.pickOutNum);
  if (!deductEnabled) return pickOutNum;

  return Math.max(pickOutNum - getNumber(row.deductOutCount), 0);
};

export const getSubmitPickOutNum = <T extends PickOutRowLike>(
  row: T,
  deductEnabled: boolean
): number => {
  const finalPickOutNum = getFinalPickOutNum(row, deductEnabled);
  const maxOutNum = Math.max(getNumber(row.maxOutNum), 0);

  return Math.min(finalPickOutNum, maxOutNum);
};

export const recalculatePickOutRowsByGenerateCount = <T extends PickOutRowLike & {
  bomAmount?: number | string | null;
  attritionRate?: number | string | null;
  defaultPickOutNum: number;
}>(rows: T[], generateCount: number | null): T[] => rows.map(row => {
  const pickOutNum = generateCount === null
    ? row.defaultPickOutNum
    : calcPickOutNumByBom(generateCount, row);
  return {
    ...row,
    pickOutNum,
    deductOutCount: clampPickOutDeductCount(row.deductOutCount, pickOutNum),
  };
});
